import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import {
  $cardListStore,
  $initialAccountsStore,
  $transferReasonsStore,
  $transferRequestsCreateStore,
  $transferRequestsFeeStore,
  $transferRequestsGetFxRateStore,
  $transferRequestsUpdateStore,
} from '@lib/store';
import { AmountUtils, cn, PaletteUtils, PaymentUtils, RoutePaths } from '@shared/utils';
import { TransferRecipientTypeEnum } from '@shared/api/rest/recipients';
import { type AccountResponse, AccountType } from '@shared/api/rest/accounts';
import { TransferRequestTypeEnum } from '@shared/api/rest/transferRequests';
import { AmountTypeEnum } from '@shared/api/rest/payment';
import { FilesFormBlock, PaymentFormBlock, TransferConfirmationBlock } from '@widgets';
import { Icon, Button, PageContainer, Text, ErrorMessage, SelectAccount, type FileWithId, type CustomFile, Spinner } from '@shared/widgets';
import { Constants } from '@shared/constants';
import ArrowIcon from '@assets/icons/arrow.svg';
import ArrowVCFCIcon from '@assets/icons/arrow-vcfc.svg';

export interface IFormDataSettlementCard {
  amountType: AmountTypeEnum;
  amount?: number;
  beneficiaryAmount?: number;
  paymentReason: string;
  purpose: string;
  files: (FileWithId | CustomFile)[];
}
const DEFAULT_VALUES: IFormDataSettlementCard = {
  amountType: AmountTypeEnum.BeneficiaryAmount,
  amount: undefined,
  purpose: '',
  beneficiaryAmount: undefined,
  paymentReason: '',
  files: [],
};

export const PaymentCardSettlementPage = () => {
  const accountStore = useUnit($initialAccountsStore.store);
  const feeStore = useUnit($transferRequestsFeeStore.store);
  const transferRequestsCreateStore = useUnit($transferRequestsCreateStore.store);
  const transferRequestsUpdateStore = useUnit($transferRequestsUpdateStore.store);
  const transferReasonsStore = useUnit($transferReasonsStore.store);

  const [account, setAccount] = useState<AccountResponse | null>(null);
  const [accountTo, setAccountTo] = useState<AccountResponse>();
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [errorSelect, setErrorSelect] = useState<string>('');

  const formId = useId();
  const formControl = useForm<IFormDataSettlementCard>({ defaultValues: DEFAULT_VALUES });
  const amount = formControl.watch('amount');
  const beneficiaryAmount = formControl.watch('beneficiaryAmount');

  const { t } = useTranslation('payments');

  useEffect(() => {
    Promise.allSettled([$transferReasonsStore.events.getTransferReasons(), $cardListStore.events.update({ Count: 100 })]);

    return () => {
      $transferRequestsCreateStore.events.clear();
      $transferRequestsGetFxRateStore.events.clear();
    };
  }, []);

  useEffect(() => {
    if (accountStore.data.length === 0) return;
    setAccount(accountStore.data[0]);
  }, [accountStore.data]);

  useEffect(() => {
    if (!account || !accountTo) return;
    if (account.currency === accountTo.currency) {
      $transferRequestsGetFxRateStore.events.setRate(1);
      return;
    }
    $transferRequestsGetFxRateStore.events.update({
      AccountId: account.id,
      FromCurrency: account.currency,
      ToCurrency: accountTo.currency,
      CountryCode: 'DE',
    });
  }, [accountTo?.currency, account?.currency]);

  const requestFeeHandler = useCallback(async () => {
    if (!amount || !accountTo) return;
    await $transferRequestsFeeStore.events.update({
      AccountId: account?.id,
      SenderAmount: amount,
      FastPayment: true,
      BeneficiaryCurrencyCode: accountTo?.currency,
      BeneficiaryBankCountryCode: 'USA',
      TransferRequestType: TransferRequestTypeEnum.CardAccountSettlement,
    });
  }, [account?.id, accountTo?.currency, amount]);
  useEffect(() => {
    requestFeeHandler();
  }, [account?.id, accountTo]);

  const onSubmit = async (data: IFormDataSettlementCard) => {
    if (!accountTo) setErrorSelect(t('form.errorMessages.requiredAccount'));
    if (!account || !accountTo) return;

    if (transferRequestsCreateStore?.data) {
      await $transferRequestsUpdateStore.events.update({
        id: transferRequestsCreateStore.data.id,
        type: TransferRequestTypeEnum.CardAccountSettlement,
        fromAccountId: account?.id,
        beneficiaryCurrency: account?.currency,
        purpose: data.purpose,
        beneficiaryAccountId: accountTo?.id,
        senderAmount: data.amount,
        totalAmount: data.amount,
        beneficiaryAmount: data.beneficiaryAmount,
        reasonOfThePayment: data.paymentReason,
        beneficiaryCountryCode: 'USA',
        fastPayment: true,
        StickToBeneficiaryAmount: data.amountType === AmountTypeEnum.BeneficiaryAmount,
      });
      if (transferRequestsCreateStore.data?.id && data.files?.length) {
        const files = await PaymentUtils.uploadPaymentDocuments({ transferRequestId: transferRequestsCreateStore.data.id, files: data.files });
        formControl.setValue('files', files);
      }
      setShowPaymentModal(true);
      return;
    }

    const newTransferRequest = await $transferRequestsCreateStore.events.create({
      type: TransferRequestTypeEnum.CardAccountSettlement,
      fromAccountId: account?.id,
      beneficiaryCurrency: account?.currency,
      purpose: data.purpose,
      beneficiaryAccountId: accountTo?.id,
      senderAmount: data.amount,
      totalAmount: data.amount,
      beneficiaryAmount: data.beneficiaryAmount,
      reasonOfThePayment: data.paymentReason,
      fastPayment: true,
      StickToBeneficiaryAmount: data.amountType === AmountTypeEnum.BeneficiaryAmount,
    });
    if (newTransferRequest.data?.id && data.files?.length) {
      const files = await PaymentUtils.uploadPaymentDocuments({ transferRequestId: newTransferRequest.data.id, files: data.files });
      formControl.setValue('files', files);
    }
    setShowPaymentModal(true);
  };

  const reasonOptions = useMemo(() => {
    const bankCountryCode = Constants.BANK_COUNTRY_CODE;
    const individualReasons = transferReasonsStore.data.filter((reason) => reason?.beneficiaryType?.includes(TransferRecipientTypeEnum.Business));
    const countryReasons = individualReasons.filter((reason) => reason.countryCode?.toUpperCase() == bankCountryCode);
    const reasons = countryReasons.length > 0 ? countryReasons : individualReasons.filter((reason) => !reason.countryCode);
    return reasons.map((reason) => ({ label: reason.name, value: reason.id }));
  }, [transferReasonsStore.data]);
  // const total = amount! + (feeStore?.data || 0);
  return (
    <PageContainer
      title={t('title')}
      header={
        <Link className='group flex items-center gap-2' to={RoutePaths.payments.onNavigate()}>
          <Icon
            icon={PaletteUtils.isInsigniaPalette ? <ArrowIcon /> : <ArrowVCFCIcon className='text-black dark:text-white' />}
            className={cn(PaletteUtils.isInsigniaPalette && 'rotate-180')}
          />
          {PaletteUtils.isInsigniaPalette ? (
            <Text weight='semibold' size='2xl'>
              {t('header')}
            </Text>
          ) : (
            <Text className='group-hover:underline' size='sm'>
              {t('return')}
            </Text>
          )}
        </Link>
      }
      return
    >
      {formControl.formState.isSubmitting && <Spinner position />}
      <FormProvider {...formControl}>
        <div className={cn('flex flex-col', PaletteUtils.isVcfcPalette && 'rounded-xl bg-white py-6 dark:bg-soft-black')}>
          <Text
            className={cn(PaletteUtils.isVcfcPalette && 'px-6 font-[NewtonCCT] text-[26px]')}
            weight={PaletteUtils.isInsigniaPalette ? 'semibold' : undefined}
            size='2xl'
          >
            {t('cardSettlement.title')}
          </Text>
          <SelectAccount
            className={cn('mt-6 bg-white p-6 dark:bg-soft-black', PaletteUtils.isVcfcPalette && 'mt-0')}
            loading={accountStore.loading}
            type={AccountType.Payment}
            accounts={accountStore.data}
            currentAccount={account}
            onChange={setAccount}
            label={t('cardSettlement.placeholders.transferFrom')}
            balanceFieldName='availableBalance'
          />
          <form id={formId} noValidate onSubmit={formControl.handleSubmit(onSubmit)}>
            <div className={cn('mt-6 bg-white p-6 dark:bg-soft-black', PaletteUtils.isVcfcPalette && 'mt-0')}>
              <SelectAccount
                loading={accountStore.loading}
                accounts={accountStore?.data.filter((a) => a.currency === 'EUR').filter((acc) => acc.id !== account?.id)}
                label={t('cardSettlement.placeholders.selectCardAccount')}
                currentAccount={accountTo}
                type={AccountType.Card}
                onChange={setAccountTo}
                balanceFieldName='availableBalance'
              />
              {accountTo ? '' : <ErrorMessage text={errorSelect} />}
            </div>
            <PaymentFormBlock
              className={cn('mt-6 bg-white p-6 dark:bg-soft-black', PaletteUtils.isVcfcPalette && 'mt-0')}
              account={account}
              accountTo={accountTo}
              currency={account?.currency}
              reasonOptions={reasonOptions}
              onRequestFee={requestFeeHandler}
            />
            <FilesFormBlock
              className={cn('mt-6 bg-white p-6 dark:bg-soft-black', PaletteUtils.isVcfcPalette && 'mt-0 pt-0')}
              transactionId={transferRequestsCreateStore.data?.id}
            />
          </form>
          <div className={cn('mt-3', PaletteUtils.isVcfcPalette && 'mt-0 px-6')}>
            <Button
              className={cn('max-w-72', PaletteUtils.isVcfcPalette && 'w-[285px]')}
              color='primary'
              type='submit'
              form={formId}
              disabled={
                feeStore.loading || transferRequestsCreateStore.loading || transferRequestsUpdateStore.loading || formControl.formState.isSubmitting
              }
            >
              {t('form.buttons.transfer')}{' '}
              {beneficiaryAmount! > 0 && `${AmountUtils.transformToAmountWithCurrency(beneficiaryAmount!, accountTo?.currency as string)}`}
            </Button>
          </div>
        </div>
        {transferRequestsCreateStore.data?.id && (
          <TransferConfirmationBlock
            showPaymentModal={showPaymentModal}
            onClosePaymentModal={() => setShowPaymentModal(false)}
            account={account!}
            accountTo={accountTo}
          />
        )}
      </FormProvider>
    </PageContainer>
  );
};
